import { apiInstance, gql } from "@/services/httpAdapter";
import { errorLogger } from "@Common/helpers/logger";
import { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";
import { Nullable } from "@Common/api/types/utilities";
import { OEMDocument } from "@DataPages/types/oem/model.ts";

type OEMDataArgs = {
	programName: string;
	locationId: string;
	commissionCycleId: string;
	oemReference: string;
	dealerCodeFS: string;
	franchiseName: string;
	page: string;
};
export async function getOEMData({
	programName,
	locationId,
	commissionCycleId,
	oemReference,
	dealerCodeFS,
	franchiseName,
	page,
}: Readonly<Partial<OEMDataArgs>>): Promise<Nullable<OEMDocument>> {
	if (programName === "Financial Statements") {
		return getOEMFinancialStatements({ locationId, commissionCycleId, oemReference, dealerCodeFS, page });
	} else {
		return getOEMDocumentsData({ locationId, commissionCycleId, programName, oemReference, dealerCodeFS, franchiseName, page });
	}
}

type OEMFinancialStatementsArgs = {
	locationId: string;
	commissionCycleId: string;
	oemReference: string;
	dealerCodeFS: string;
	page: string;
};

async function getOEMFinancialStatements({
	locationId,
	commissionCycleId,
	oemReference,
	dealerCodeFS,
	page,
}: Readonly<Partial<OEMFinancialStatementsArgs>>) {
	const query = gql`
		query getOEMFinancialStatements(
			$locationId: String!
			$commissionCycleId: String!
			$oemReference: String
			$dealerCodeFS: String
			$page: String
		) {
			getOEMFinancialStatements(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				oemReference: $oemReference
				dealerCodeFS: $dealerCodeFS
				page: $page
			) {
				locationId
				commissionCycleId
				pages {
					oemReference
					dealerCodeFS
					oemName
					pageNames
					page {
						_id
						pageName
						pageNumber
						pageInformation {
							icon {
								iconName
								styles {
									width
									marginRight
								}
							}
						}
						content
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		oemReference,
		dealerCodeFS,
		page,
	};

	try {
		const response: GraphqlQueryResponse<"getOEMFinancialStatements", OEMDocument> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getOEMFinancialStatements;
	} catch (err) {
		errorLogger(err as Error, { function: "getOEMFinancialStatements", message: "Error fetching OEM Financial Statements", variables });
		return null;
	}
}

type OEMDocumentsDataArgs = {
	locationId: string;
	commissionCycleId: string;
	programName: string;
	oemReference: string;
	dealerCodeFS: string;
	franchiseName: string;
	page: string;
};
async function getOEMDocumentsData({
	locationId,
	commissionCycleId,
	programName,
	oemReference,
	dealerCodeFS,
	franchiseName,
	page,
}: Readonly<Partial<OEMDocumentsDataArgs>>) {
	const query = gql`
		query getOEMDocumentsData(
			$locationId: String!
			$commissionCycleId: String!
			$programName: String!
			$oemReference: String
			$dealerCodeFS: String
			$franchiseName: String
			$page: String
		) {
			getOEMDocumentsData(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				programName: $programName
				oemReference: $oemReference
				dealerCodeFS: $dealerCodeFS
				franchiseName: $franchiseName
				page: $page
			) {
				locationId
				commissionCycleId
				pages {
					oemReference
					dealerCodeFS
					oemName
					dealerCodeFS
					franchiseNames
					franchise
					pageNames
					page {
						_id
						pageName
						pageNumber
						pageInformation {
							icon {
								iconName
								styles {
									width
									marginRight
								}
							}
						}
						content
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		programName,
		oemReference,
		dealerCodeFS,
		franchiseName,
		page,
	};

	try {
		const response: GraphqlQueryResponse<"getOEMDocumentsData", OEMDocument> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getOEMDocumentsData;
	} catch (err) {
		errorLogger(err as Error, { function: "getOEMDocumentsData", message: "Error fetching OEM Documents Data", variables });
		return null;
	}
}

type franchisesByProgram = { program: string; franchises: string[], hideSubmenu: boolean };
export type OEMMetaMetric = {
	oemName: string;
	metrics: string;
	franchisesByProgramByOem: [{ oemDisplayName: string; franchisesByProgram: franchisesByProgram[] }];
	programsDepartments: {
		departments: string[];
		name: string;
	}[];
	disabledMarketShares: string[];
};
export async function getOemMetaMetrics(locationId: string, commissionCycleId: string) {
	const query = gql`
		query getOemMetaMetrics($locationId: String!, $commissionCycleId: String!) {
			getOemMetaMetrics(locationId: $locationId, commissionCycleId: $commissionCycleId) {
				oemName
				metrics
				franchisesByProgramByOem {
					oemDisplayName
					franchisesByProgram {
						program
						franchises
						hideSubmenu
					}
				}
				programsDepartments {
					departments
					name
				}
				disabledMarketShares
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
	};

	try {
		const response: GraphqlQueryResponse<"getOemMetaMetrics", OEMMetaMetric[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getOemMetaMetrics;
	} catch (err) {
		errorLogger(err as Error, { function: "getOemMetaMetrics", message: "Error fetching OEM Meta Metrics", variables });
		return null;
	}
}

type UploadFinancialStatementArgs = {
	locationId: string;
	commissionCycleId: string;
	file: File;
	options: {
		bucketEnvVariable: string;
		action: string;
		contentType: string;
		filePathKeys: string[];
		fileNameKeys: string[];
		franchise: string | null;
		dealerCodeFS: string | undefined;
		oemReference: string | undefined;
	};
};
export async function uploadFinancialStatement({ locationId, commissionCycleId, file, options }: Readonly<UploadFinancialStatementArgs>) {
	try {
		const query = gql`
			query getPresignedURL($locationId: String!, $commissionCycleId: String!, $options: CommonOptionsInput) {
				getPresignedURL(locationId: $locationId, commissionCycleId: $commissionCycleId, options: $options) {
					fileName
					presignedUrl
					error {
						status
						message
					}
				}
			}
		`;

		const variables = {
			locationId,
			commissionCycleId,
			options,
		};

		const response = await apiInstance.post("/graphql", { query, variables });
		const preSigned = response.data?.data?.getPresignedURL;

		if (preSigned?.presignedUrl) {
			const fileName = preSigned?.fileName;
			const uploadFile = new File([file], fileName);

			const requestOptions = {
				headers: {
					"Content-Type": file.type,
				},
			};
			const upload = await apiInstance.put(preSigned?.presignedUrl, uploadFile, requestOptions);
			const successStatusCode = 200;
			const serverErrorStatusCode = 500;
			if (upload.status === successStatusCode) {
				return true;
			}

			return {
				status: serverErrorStatusCode,
				message: `Something went wrong.`,
			};
		} else {
			return {
				status: preSigned?.error?.status,
				message: preSigned?.error?.message,
			};
		}
	} catch (error) {
		errorLogger(error as Error, { function: "uploadFinancialStatement", message: "Error uploading financial statement" });
		return null;
	}
}
